import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAppContext } from "@/context/AppContext";
import { useBreadcrumbs } from "@/context/BreadcrumbContext";
import { parseQuickAddText } from "@/lib/quickAddHelpers";
import { getTablePayload, resolveTableView } from "@/lib/tableBlockHelpers";
import { encrypt } from "@/utls/CryptoHelpers";

function formatPreviewValue(value: unknown): string {
  if (value === undefined || value === null) return "";
  return String(value);
}

export function TableQuickAddRoute() {
  const { id: tableId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { blocks, isLoading, addDecryptedBlock } = useAppContext();
  const { setBreadcrumbSegments } = useBreadcrumbs();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [text, setText] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const viewKey = searchParams.get("view");

  const tableModel = useMemo(() => {
    if (!tableId) return null;
    const tableBlock = blocks.find((b) => b.id === tableId && b.type === "table");
    if (!tableBlock) return null;
    const tableData = getTablePayload(tableBlock.data);
    if (!tableData) return null;
    const activeView = resolveTableView({
      viewKey: viewKey && viewKey.length > 0 ? viewKey : null,
      defaultView: tableData.defaultView,
      views: tableData.views ?? [],
    });
    return {
      name: tableData.name ?? "",
      schema: tableData.schema,
      extractRegex: activeView?.quickAdd?.extractRegex,
    };
  }, [blocks, tableId, viewKey]);

  const breadcrumbTableLabel = isLoading
    ? "…"
    : tableModel
      ? tableModel.name || "Table"
      : "Table not found";

  useEffect(() => {
    if (!tableId) {
      setBreadcrumbSegments([]);
      return () => setBreadcrumbSegments([]);
    }
    setBreadcrumbSegments([
      { label: "Tables", to: "/tables" },
      { label: breadcrumbTableLabel, to: `/tables/${tableId}` },
      { label: "Quick Add" },
    ]);
    return () => setBreadcrumbSegments([]);
  }, [breadcrumbTableLabel, setBreadcrumbSegments, tableId]);

  const parseResult = useMemo(
    () =>
      parseQuickAddText({
        text,
        extractRegex: tableModel?.extractRegex,
        schema: tableModel?.schema ?? [],
      }),
    [tableModel?.extractRegex, tableModel?.schema, text],
  );

  const handleAdd = useCallback(async () => {
    if (!tableId || !tableModel || parseResult.status !== "ok" || isAdding) {
      return;
    }
    const secretKey =
      typeof window !== "undefined" ? (window.localStorage.accessKey ?? "") : "";
    if (!secretKey) {
      console.warn(
        "TableQuickAddRoute: no accessKey in localStorage; cannot create block",
      );
      return;
    }

    const initialData = {
      parent: tableId,
      data: parseResult.rowData,
    };
    setIsAdding(true);
    try {
      const encryptedData = await encrypt(JSON.stringify(initialData), secretKey);
      const res = await fetch("/api/blocks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ encryptedData }),
      });
      if (!res.ok) {
        console.error("TableQuickAddRoute: create block failed", await res.text());
        return;
      }
      const parsed = (await res.json()) as { block?: { _id?: string } };
      const newId = parsed.block?._id;
      if (!newId) {
        console.error("TableQuickAddRoute: create block missing id in response");
        return;
      }
      addDecryptedBlock({
        block: {
          id: newId,
          data: initialData,
        },
      });
      const viewQuery = viewKey ? `?view=${encodeURIComponent(viewKey)}` : "";
      navigate(`/tables/${tableId}/blocks/${newId}${viewQuery}`);
    } finally {
      setIsAdding(false);
    }
  }, [
    addDecryptedBlock,
    isAdding,
    navigate,
    parseResult,
    tableId,
    tableModel,
    viewKey,
  ]);

  if (!isLoading && !tableModel) {
    return (
      <p className="text-muted-foreground p-8 text-sm">
        This table does not exist or could not be loaded.
      </p>
    );
  }

  const previewMessage =
    parseResult.status === "missing-regex"
      ? "This view has no extract regex. Add one under Tables → Edit → Views."
      : parseResult.status === "invalid-regex"
        ? `Invalid extract regex: ${parseResult.error}`
        : parseResult.status === "empty-text"
          ? "Paste text to preview the imported fields."
          : parseResult.status === "no-match"
            ? "The pasted text does not match the extract regex."
            : null;

  return (
    <div className="mx-auto flex w-full min-w-[20rem] max-w-2xl flex-1 flex-col p-6">
      <div className="mb-3 text-xl font-bold">Quick Add</div>

      <div className="flex flex-col gap-6">
        <div className="space-y-2">
          <Textarea
            ref={textareaRef}
            id="quick-add-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            className="font-mono"
            placeholder="Paste text to extract…"
            autoFocus
          />
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Preview</div>
          {parseResult.status === "ok" ? (
            <dl className="divide-border divide-y rounded-md border">
              {parseResult.fields.map((field) => (
                <div
                  key={field.key}
                  className="flex items-baseline justify-between gap-4 px-3 py-2"
                >
                  <dt className="text-muted-foreground shrink-0 text-sm">
                    {field.name}
                    {field.source === "autofill" ? (
                      <span className="ml-1.5 text-xs">autofill</span>
                    ) : null}
                  </dt>
                  <dd className="min-w-0 text-right text-sm break-words">
                    {formatPreviewValue(field.value)}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-muted-foreground text-sm">{previewMessage}</p>
          )}
        </div>

        <div>
          <Button
            type="button"
            disabled={parseResult.status !== "ok" || isAdding}
            onClick={() => void handleAdd()}
          >
            {isAdding ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : null}
            Add
          </Button>
        </div>
      </div>
    </div>
  );
}
